import { jsx as _jsx } from "react/jsx-runtime";
import { LinkingContext, useNavigationBuilder } from "@react-navigation/native";
import { Children, Fragment, isValidElement, use, useMemo, useRef } from "react";
import { useRouteInfo } from "../hooks";
import { resolveHref } from "../link/href";
import { useContextKey, useRouteNode } from "../router/Route";
import { shouldLinkExternally } from "../utils/url";
import { NavigatorContext } from "../views/Navigator";
import { triggersToScreens } from "./common";
import { TabTriggerMapContext } from "./TabContext";
import { isTabList } from "./TabList";
import { ExpoTabRouter } from "./TabRouter";
import { isTabSlot } from "./TabSlot";
import { isTabTrigger } from "./TabTrigger";
import { useComponent } from "./useComponent";
function useTabsWithChildren(options) {
  var { children, ...rest } = options;
  return useTabsWithTriggers({
    triggers: parseTriggersFromChildren(children),
    ...rest
  });
}
function useTabsWithTriggers(options) {
  var { triggers, ...rest } = options;
  var parentTriggerMap = use(TabTriggerMapContext);
  var routeNode = useRouteNode();
  var contextKey = useContextKey();
  var linking = use(LinkingContext).options;
  var routeInfo = useRouteInfo();
  if (!routeNode || !linking) {
    throw new Error("No RouteNode. This is likely a bug in one router.");
  }
  var { children, triggerMap } = triggersToScreens(triggers, routeNode, linking, routeNode.initialRouteName, parentTriggerMap, routeInfo, contextKey);
  var navigatorContext = useNavigationBuilder(ExpoTabRouter, {
    children,
    ...rest,
    triggerMap,
    initialRouteName: routeNode.initialRouteName
  });
  var { state, descriptors, navigation, render } = navigatorContext;
  var descriptorsRef = useRef(descriptors);
  descriptorsRef.current = descriptors;
  var navigatorContextValue = useMemo(function() {
    return {
      state,
      navigation,
      contextKey,
      router: ExpoTabRouter,
      descriptorsRef
    };
  }, [
    state,
    navigation,
    contextKey
  ]);
  var NavigationContent = useComponent(function(children2) {
    return /* @__PURE__ */ _jsx(TabTriggerMapContext.Provider, {
      value: triggerMap,
      children: /* @__PURE__ */ _jsx(NavigatorContext.Provider, {
        value: navigatorContextValue,
        children: render(children2)
      })
    });
  });
  return {
    state,
    descriptors,
    navigation,
    NavigationContent
  };
}
function parseTriggersFromChildren(children) {
  var screenTriggers = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : [], isInTabList = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false;
  Children.forEach(children, function(child) {
    if (!child || !/* @__PURE__ */ isValidElement(child) || isTabSlot(child)) return;
    if (isFragment(child) && typeof child.props.children !== "function") {
      return parseTriggersFromChildren(child.props.children, screenTriggers, isInTabList || isTabList(child));
    }
    if (isTabList(child) && typeof child.props.children !== "function") {
      var _$children = child.props.children;
      if (child.props.asChild && /* @__PURE__ */ isValidElement(_$children) && _$children.props && typeof _$children.props === "object" && "children" in _$children.props) {
        _$children = _$children.props.children;
      }
      return parseTriggersFromChildren(_$children, screenTriggers, isInTabList || isTabList(child));
    }
    if (!isInTabList || !isTabTrigger(child)) return;
    var { href, name } = child.props;
    if (!href) {
      if (process.env.NODE_ENV === "development") {
        console.warn(`<TabTrigger name={${name}}> does not have a 'href' prop. TabTriggers within a <TabList /> are required to have an href.`);
      }
      return;
    }
    var resolvedHref = resolveHref(href);
    if (shouldLinkExternally(resolvedHref)) {
      screenTriggers.push({
        type: "external",
        name,
        href: resolvedHref
      });
      return;
    }
    if (!name) {
      if (process.env.NODE_ENV === "development") {
        console.warn(`<TabTrigger> does not have a 'name' prop. TabTriggers within a <TabList /> are required to have a name.`);
      }
      return;
    }
    screenTriggers.push({
      type: "internal",
      href: resolvedHref,
      name
    });
  });
  return screenTriggers;
}
function isFragment(child) {
  return child.type === Fragment;
}
export {
  useTabsWithChildren,
  useTabsWithTriggers
};
//# sourceMappingURL=Tabs.shared.js.map
